import { createContext, useCallback, useContext, useState } from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { X } from 'lucide-react';

interface Toast {
  id: string;
  message: string;
  persistent?: boolean;
  variant?: 'default' | 'bee';
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastContextValue {
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    setToasts((prev) => [...prev, { ...toast, id }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      <ToastPrimitive.Provider swipeDirection="right">
        {children}

        {toasts.map((toast) => {
          const isBee = toast.variant === 'bee';

          return (
            <ToastPrimitive.Root
              key={toast.id}
              // persistent toasts should not auto-close
              duration={toast.persistent ? Infinity : 5000}
              onOpenChange={(open: boolean) => {
                if (!open) removeToast(toast.id);
              }}
              className={`
                group pointer-events-auto relative flex w-full items-start gap-3
                overflow-hidden rounded-xl border-2 p-4 shadow-lg transition-all
                data-[swipe=cancel]:translate-x-0
                data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)]
                data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]
                data-[state=open]:animate-in data-[state=open]:slide-in-from-right-full
                data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right-full
                ${isBee
                  ? 'border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50'
                  : 'border-gray-200 bg-white'
                }
              `}
              data-testid={`toast-${toast.id}`}
            >
              {isBee && (
                <div className="flex-shrink-0" data-testid="toast-bee-icon">
                  <img
                    src="/logo-192.png"
                    alt="Biz Bee"
                    className="w-10 h-10 object-contain animate-bounce-slow"
                  />
                </div>
              )}

              <div className="flex-1 min-w-0">
                <ToastPrimitive.Description
                  className={`text-sm font-medium ${isBee ? 'text-amber-900' : 'text-gray-700'}`}
                  data-testid="toast-message"
                >
                  {toast.message}
                </ToastPrimitive.Description>
                {toast.action && (
                  <button
                    onClick={() => {
                      toast.action!.onClick();
                      removeToast(toast.id);
                    }}
                    className={`
                      mt-2 text-xs font-semibold underline underline-offset-2 transition-colors
                      ${isBee ? 'text-amber-700 hover:text-amber-900' : 'text-sky-600 hover:text-sky-800'}
                    `}
                    data-testid="toast-action-btn"
                  >
                    {toast.action.label}
                  </button>
                )}
              </div>

              <ToastPrimitive.Close
                className={`
                  flex-shrink-0 rounded-md p-1 transition-colors
                  ${isBee
                    ? 'text-amber-600 hover:bg-amber-200/50 hover:text-amber-800'
                    : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                  }
                `}
                aria-label="Dismiss"
                data-testid="toast-dismiss-btn"
              >
                <X className="h-4 w-4" />
              </ToastPrimitive.Close>
            </ToastPrimitive.Root>
          );
        })}

        <ToastPrimitive.Viewport
          className="fixed bottom-4 right-4 z-[100] flex max-h-screen w-full max-w-sm flex-col gap-2 outline-none"
          data-testid="toast-viewport"
        />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}
